import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import SectionDetails from '../../_Sections/SectionDetails';
import SectionGallery from '../../_Sections/SectionGallery';
import SectionDropship from '../../_Sections/SectionDropship';
import SectionPairings from '../../_Sections/SectionPairings';
import SectionBranding from '../../_Sections/SectionBranding';
import SectionSizes from '../../_Sections/SectionSizes';
import SectionPricing from '../../_Sections/SectionPricing';
import tKey from '../../../Common/Utils/tKey';
import { useAppSelector } from '../../../Common/_hooks/useAppSelector';
import ProductSEO from '../../_Sections/ProductSEO';
import SizeDisplay from '../../../Common/Measurements/SizeDisplay';

const PRODUCT_ID = 12;
const VIEW_PRODUCT_ID = 20;

export default function LeggingsPage() {
  const [t] = useTranslation('page/products/leggings');
  const productData = useAppSelector(state => state.get('appData').get('products').get(String(PRODUCT_ID)));
  const locale = useAppSelector(state => state.get('userData').get('prefs').get('lang'))

  if (!productData) return null

  return <div className="centered-page product-page-v4">
    <ProductSEO
      viewProductId={VIEW_PRODUCT_ID}
      title={t('Custom all-over print Leggings | Products')}
      description={t('Design and create your own custom printed Leggings. ✔ Print-on-demand drop shipping ✔ No minimums ✔ Made in Canada')}
    />
    <SectionDetails
      viewProductId={VIEW_PRODUCT_ID}
      color="#ff8783"
      bgStyle={{
        backgroundPosition: 'top right',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '47%',
      }}
      tag={t('The all-over print')}
      title={t('Leggings')}
      text={t('Step into comfort with custom print on-demand leggings that contour and support. Made from ultra-smooth Luxeform performance fabric with a foldover elastic waistband they offer a flattering fit right down to your ankle. Each pair of custom leggings is cut and sewn on-demand in our Montreal studio.')}
      createUrl={productData.get('url_slug') + '?option_fabric=performance-knit'}
      img={require('@resources/img/pages/products/leggings/banner-1-' + locale + '.png')}
      alt={t('Picture of Leggings')}
      details={[
        {
          img: require('@resources/img/pages/products/leggings/icon-1.svg?url'),
          alt: t('icon'),
          text: <Trans
            t={t}
            i18nKey={tKey('Highrise, body-hugging fit with <size/> foldover elastic waistband.')}
            components={{
              size: <SizeDisplay sizes={[1.5]} sourceUnit="in" displayType="physical" />,
            }}
          />,
        },
        {
          img: require('@resources/img/pages/products/leggings/icon-2.svg?url'),
          alt: t('icon'),
          text: t('Made from Luxeform performance fabric: combining the power of spandex with lightness and comfort'),
        },
        {
          img: require('@resources/img/pages/products/leggings/icon-3.svg?url'),
          alt: t('icon'),
          text: t('Ankle length'),
        },
        {
          img: require('@resources/img/pages/products/leggings/icon-4.svg?url'),
          alt: t('icon'),
          text: t('Vibrant, permanent prints won’t fade with use or washing'),
        },
        {
          img: require('@resources/img/pages/products/leggings/icon-5.svg?url'),
          alt: t('icon'),
          text: t('Digitally printed on demand in Montreal, Canada'),
        },
      ]}
    />
    <SectionGallery images={[
      require('@resources/img/pages/products/leggings/gallery_0.jpg'),
      require('@resources/img/pages/products/leggings/gallery_1.jpg'),
      require('@resources/img/pages/products/leggings/gallery_2.jpg'),
      require('@resources/img/pages/products/leggings/gallery_3.jpg'),
      require('@resources/img/pages/products/leggings/gallery_4.jpg'),
    ]} />

    <SectionDropship
      title={<Trans
        t={t}
        i18nKey={tKey('<yellow>Drop ship</yellow> your athletic<br/>crop top <pink>on-demand</pink>')}
        components={{
          yellow: <span className="ty" />,
          pink: <span className="tp" />,
        }}
      />}
      text={t('Your brand and following will love Leggingss! At prices designed for reselling, Leggings made on demand meet the needs of sellers who prefer not to stock inventory. Sell them in your eCommerce store and decide your profit margin.')}
    />

    <SectionPricing
      productId={PRODUCT_ID}
      options={['performance-knit']}
    />

    <SectionSizes
      chart="bottoms"
      productId={PRODUCT_ID}
      title={t('Leggings Sizing')}
      text={t('There may be some variations in product measurements within or between orders. Each garment is individually cut and sewn which can lead to minor differences in measurements. Measurements are body measurements that correspond with the garment size in US standard sizing. For in between sizes, we recommend choosing the larger size.')}
    />

    <SectionBranding
      hasLabel
      title={t('Brand your Leggings')}
      text={t('Branding enhances your products. Add custom labels, artist cards or stickers to your packages to create a cohesive message and build your brand.')}
    />

    <SectionPairings
      text={t('Pair your Leggings with these great products to create your own unique styles.')}
      productSlugs={['athletic-crop-top', 'premium-pullover-hoodie', 'slim-fit-t-shirt']}
    />
  </div>
}
